/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx, useTheme } from '@emotion/react';
import React, { useMemo } from 'react';

import { useMiddlewareContext } from '../../hooks/useMiddlewareContext';
import { IDayToDropProps } from '../../types';
import { Droppable } from '../Droppable';
import { weekDayToDropContainer } from '../styles';
import { AddNewSlotButtonsContainer } from './AddNewSlotButtons';
import { DayTimeGridSurface } from './DayTimeGridSurface';
import { DraggableSlot } from './DraggableSlot';
import { SlotGroups } from './SlotGroup';
import { LoadingDailyContainer } from '../LoadingDailyContainer';
import { useResourceSlots } from '../../hooks/useResourceSlots';
import { DEFAULT_NEW_SLOT_HOUR_SIZE } from '../../utils/timeUtils';

const MAX_COUNT = 4;

export const DayToDrop: React.FC<IDayToDropProps> = React.memo(
	({
		id,
		EventComponent,
		withoutChildren,
		isModeChanged,
		onAddNewSlotClick,
		currentDate,
		isLoading,
		activityTimes,
	}) => {
		const { slots, updateSlotData } = useResourceSlots({ resourceId: Number(id) });

		const { getSlotGroups, options } = useMiddlewareContext();
		const colors = useTheme();

		const hourSize: number = options?.hourSize?.vertical ?? DEFAULT_NEW_SLOT_HOUR_SIZE;

		const eventsGroups = useMemo(() => {
			if (!isModeChanged) {
				if (slots?.length) {
					return getSlotGroups(slots, false, currentDate!);
				}
			}
			return [];
		}, [slots, isModeChanged, options.date, hourSize, getSlotGroups, currentDate]);

		return (
			<div css={weekDayToDropContainer(colors, withoutChildren)}>
				<DayTimeGridSurface
					hourSize={hourSize}
					gridLineStepMinutes={options?.gridLineStepMinutes}
					axis="vertical"
					slotButtons={
						isLoading ? undefined : (
							<AddNewSlotButtonsContainer
								parentId={id}
								onClick={onAddNewSlotClick}
								activityTimes={activityTimes}
								hourSize={hourSize}
								date={currentDate}
							/>
						)
					}
				>
					{isLoading ? (
						<LoadingDailyContainer />
					) : (
						<Droppable id={id}>
							{eventsGroups.map((group, index) => {
								const withModal: boolean = (group.groups?.length || 0) > MAX_COUNT;

								if (Array.isArray(group.groups)) {
									return (
										<SlotGroups
											key={`SlotGroups-${index}`}
											group={group}
											withModal={withModal}
											index={index}
											id={id}
											EventComponent={EventComponent}
											colors={colors}
											currentDate={currentDate}
											mode={options.mode}
											updateSlotData={updateSlotData}
										/>
									);
								} else if (group.item) {
									const slot = group.item;
									return (
										<DraggableSlot
											key={`Draggable-Slot-${slot.id}`}
											slot={slot}
											parentId={id}
											EventComponent={EventComponent}
											currentDate={currentDate}
											mode={options.mode}
											updateSlotData={updateSlotData}
										/>
									);
								} else {
									return null;
								}
							})}
						</Droppable>
					)}
				</DayTimeGridSurface>
			</div>
		);
	}
);

DayToDrop.displayName = 'DayToDrop';
